import * as React from 'react'
import { useEffect, useRef } from 'react'
import { Card, CardContent } from '@mui/material'

import './index.scss'
import { useAppDispatch, useAppSelector } from '../../redux'
import moment from 'moment/moment'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import { FETCH_INTERVAL } from '../../utils'
import {
  changeActiveTipsTab,
} from '../../redux/slices/fixtures'
import { useNavigate } from 'react-router-dom'
import SyncIcon from '@mui/icons-material/Sync'
import { getLiveTips, updateGameStatus } from '../../redux/slices/live-tips'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import PullToRefresh from 'react-simple-pull-to-refresh'

export default function Tips() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { tips } = useAppSelector((state) => state.live_tips)
  const { user } = useAppSelector((state) => state.account)
  const { activeTipsTab } = useAppSelector((state) => state.fixtures)

  const onClickGame = async (event_id) => {
    return navigate(`/football/match/${event_id}`)
  }

  const handleChangeTab = (tabId: any) => {
    dispatch(changeActiveTipsTab(tabId))
  }
  const getActiveTab = (tab = 'all') => (activeTipsTab === tab ? 'active' : '')

  const pending = tips.filter((t) => t.status === 'pending')
  const finished = tips.filter((t) => t.status !== 'pending')

  let renderTips = activeTipsTab === 'live' ? pending : finished
  const getStatusColor = (tip) => {
    if (['win', 'won'].includes(tip.status)) {
      return 'var(--won-color)'
    }

    if (tip.status === 'lost') {
      return 'var(--lost-color)'
    }

    return 'var(--gradient-from)'
  }

  useEffect(() => {
    dispatch(getLiveTips())

    // Function to handle visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // The page has become visible, refresh your content
        dispatch(getLiveTips())
      }
    }

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange)

    window['get_live_tips_interval'] = setInterval(() => {
      dispatch(getLiveTips())
    }, FETCH_INTERVAL)

    // Clean up the event listeners and intervals on component unmount
    return () => {
      if (window['get_live_tips_interval']) {
        clearInterval(window['get_live_tips_interval'])
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, []) // Empty dependency array to run the effect only once

  const updateBetStatus = async (bet: any, status: string) => {
    dispatch(updateGameStatus({ id: bet.tip_id, gameId: bet.id, status })).then(() =>
      dispatch(getLiveTips())
    )
  }

  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: renderTips.length,
    getScrollElement: () => parentRef.current,
    measureElement: (el) => el.getBoundingClientRect().height, // Measure the height of each item dynamically
    estimateSize: () => 100, // A rough estimate the virtualizer will correct this after measuring
    overscan: 5, // Load a few extra items in advance to make scrolling smoother
  })

  const handleRefresh = async () => {
    await dispatch(getLiveTips())
  }

  return (
    <div className="live-tips-wrapper">
      <div className="tabs">
        <div
          className={'card-shadow tab-item ' + getActiveTab('live')}
          onClick={() => handleChangeTab('live')}
        >
          Live ({pending.length})
        </div>
        <div
          className={'card-shadow tab-item ' + getActiveTab('all')}
          onClick={() => handleChangeTab('all')}
        >
          Predictions ({finished.length})
        </div>
      </div>

      <PullToRefresh onRefresh={handleRefresh}>
        <div
          className="live-games"
          ref={parentRef}
          style={{ height: `calc(100vh - 128px)`, overflow: 'auto' }}
        >
          {renderTips?.length === 0 && (
            <div className="no-available-games">
              <FactCheckIcon />
              <h4>No live tips available at this moment.</h4>
              <p>Please check again later.</p>
            </div>
          )}

          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const tip = renderTips[virtualRow.index]
              return (
                <div
                  key={'history-' + tip.id}
                  data-index={virtualRow.index} // Needed for dynamic row height measurement
                  ref={(node) => rowVirtualizer.measureElement(node)} // Measure dynamic row height
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <Card variant="outlined" className="tips-card">
                    <CardContent style={{ overflow: 'hidden', padding: 0 }}>
                      <div
                        className="prediction-header"
                        style={{
                          background:
                            'linear-gradient(255.96deg, var(--gradient-to), ' +
                            getStatusColor(tip) +
                            ')',
                        }}
                      >
                        <div className="odds">{tip.title}</div>
                        <div className="odds">{(Math.round(tip.odds) / 100).toFixed(2)}</div>
                      </div>
                      <div
                        style={{
                          width: '100%',
                          borderRadius: '10px',
                          color: '#000000',
                          position: 'relative',
                        }}
                      >
                        {tip.games.map((bet: any, index: number) => (
                          <div
                            key={'history-tips-game-' + bet.id}
                            className="live"
                            onClick={() => onClickGame(bet.event_id)}
                          >
                            <div
                              className="league"
                              style={{
                                minHeight: '15px',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <div>{bet.tournament}</div>
                              <div>{moment(tip.timestamp).tz(moment.tz.guess()).fromNow()}</div>
                            </div>
                            <div className="teams-info">
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div
                                  className={
                                    'team' + (bet.score?.home > bet.score?.away ? ' in-front' : '')
                                  }
                                >
                                  <span>
                                    {bet.final_score ? bet.final_score.split('-')[0] : '-'}
                                  </span>
                                  <div className="name">{bet.home.replace('(', ' (')}</div>
                                </div>
                                <div
                                  className={
                                    'team' + (bet.score?.home < bet.score?.away ? ' in-front' : '')
                                  }
                                >
                                  <span>
                                    {bet.final_score ? bet.final_score.split('-')[1] : '-'}
                                  </span>
                                  <div className="name">{bet.away.replace('(', ' (')}</div>
                                </div>
                              </div>
                              <div className="score-wrapper">
                                <Menu as="div" className="relative inline-block text-left">
                                  <MenuButton className={`result ${bet.status}`}>
                                    {bet.status === 'live' ? (
                                      <SyncIcon />
                                    ) : (
                                      bet.status.toUpperCase().charAt(0)
                                    )}
                                  </MenuButton>
                                  {user && (
                                    <MenuItems
                                      transition
                                      className="absolute right-0 z-10 mt-2 origin-top-right"
                                    >
                                      <div className="py-1">
                                        <MenuItem>
                                          <div
                                            className={`result ${
                                              bet.status === 'win' ? 'lost' : 'win'
                                            }`}
                                            onClick={() =>
                                              updateBetStatus(
                                                bet,
                                                bet.status === 'win' ? 'lost' : 'win'
                                              )
                                            }
                                          >
                                            {bet.status === 'win' ? 'L' : 'W'}
                                          </div>
                                        </MenuItem>
                                      </div>
                                    </MenuItems>
                                  )}
                                </Menu>
                              </div>
                            </div>
                            <hr />
                            <div className="prediction-wrapper">
                              <div>
                                {bet.market} {bet.pick}
                              </div>
                              <div className="odds">
                                {(Math.round(bet.odds) / 100).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            })}
          </div>
        </div>
      </PullToRefresh>
    </div>
  )
}
